import { useReactFlow, getNodesBounds } from "reactflow"
import { toPng, toSvg } from "html-to-image"
import * as Sentry from "@sentry/react"

import { Button } from "@aix/ui"

interface Props {
  fileName: string
}

export function ExportButton({ fileName }: Props) {
  const { getNodes } = useReactFlow()

  const doExport = async (format: "png" | "svg") => {
    const nodes = getNodes()
    if (nodes.length === 0) return

    const activeNodes = nodes.filter((node) => !node.data?.isContextNode)
    const exportNodes = activeNodes.length > 0 ? activeNodes : nodes
    const bounds = getNodesBounds(exportNodes)
    const padding = 32
    const width = bounds.width + padding * 2
    const height = bounds.height + padding * 2

    const el = document.querySelector<HTMLElement>(".react-flow__viewport")
    if (!el) return

    const opts = {
      backgroundColor: "#ffffff",
      width,
      height,
      style: {
        background: "#ffffff",
        color: "#0f172a",
        colorScheme: "light",
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${-bounds.x + padding}px, ${-bounds.y + padding}px)`,
        transformOrigin: "top left",
      },
      filter: (node: Element) => {
        const cls = node.classList
        if (!cls) return true
        return (
          !cls.contains("react-flow__minimap") &&
          !cls.contains("react-flow__controls") &&
          !cls.contains("react-flow__attribution")
        )
      },
    }

    const baseName = fileName.replace(".json", "") || "diagram"
    const root = document.documentElement
    const body = document.body
    const hadDarkClass = root.classList.contains("dark")
    const previousColorScheme = root.style.colorScheme
    const previousBodyBackground = body.style.backgroundColor

    try {
      root.classList.remove("dark")
      root.style.colorScheme = "light"
      body.style.backgroundColor = "#ffffff"

      if (format === "png") {
        const dataUrl = await toPng(el, opts)
        downloadDataUrl(dataUrl, `${baseName}.png`)
      } else {
        const dataUrl = await toSvg(el, opts)
        downloadDataUrl(dataUrl, `${baseName}.svg`)
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "diagrammer-export", format } })
      return
    } finally {
      if (hadDarkClass) root.classList.add("dark")
      root.style.colorScheme = previousColorScheme
      body.style.backgroundColor = previousBodyBackground
    }
  }

  return (
    <div className="flex gap-1">
      <Button variant="outline" size="sm" onClick={() => doExport("png")} className="flex-1">
        PNG
      </Button>
      <Button variant="outline" size="sm" onClick={() => doExport("svg")} className="flex-1">
        SVG
      </Button>
    </div>
  )
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a")
  a.setAttribute("download", filename)
  a.setAttribute("href", dataUrl)
  a.click()
}
